import upperFirst from 'lodash/upperFirst';
import i18n from 'i18next';
import moment from 'moment';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

import enTranslation from 'locales/en/translation.json';

// The UI languages this build ships. Single source of truth: it drives i18next's
// `supportedLngs`, the picker's options, and the clamp in setLanguage — so a
// language can't be half-added. Ordered as the picker lists them. Adding one also
// needs a lazyCatalogs entry (and a lazyMomentLocales entry if moment ships the
// locale) plus a label in the picker's LANGUAGE_LABELS.
export const SUPPORTED_LANGUAGES = ['en', 'es', 'de', 'nl', 'fr', 'pl'];

// Reduce an arbitrary BCP-47 tag to a language we can actually render, falling
// back to English. Region-suffixed tags (`es-ES`, `es-419`) resolve to their base
// language rather than to the fallback, matching how i18next resolves them
// through supportedLngs.
export function resolveSupportedLanguage(lng) {
  const baseLng = (lng || '').split('-')[0];
  return SUPPORTED_LANGUAGES.includes(baseLng) ? baseLng : 'en';
}

// Translation key strategy: we use the English source string as the i18n key
// (keySeparator: false, nsSeparator: false). Locale files must match the English
// string exactly, including any Trans `<tag>{{var}}</tag>` markup and `{{var}}`
// placeholders. Any drift (whitespace, punctuation, tag rename, placeholder
// rename) silently breaks the lookup and falls back to the English source — no
// build error. When editing a phrase, update every locale JSON to match the
// new exact string. Pluralization uses i18next CLDR plural suffixes
// (`key_one`, `key_other`, `key_few`, ...) via `t(key, { count })`, not manual
// ternaries — see session_ended_item.jsx / custom_attribute_change_item.jsx.
// Only the default (en) catalog ships in the main bundle; other locales are
// code-split and fetched on demand when the language changes. Static import
// strings (not a template path) so both webpack and vite can split the chunks.
const lazyCatalogs = {
  es: () => import('locales/es/translation.json'),
  de: () => import('locales/de/translation.json'),
  nl: () => import('locales/nl/translation.json'),
  fr: () => import('locales/fr/translation.json'),
  pl: () => import('locales/pl/translation.json'),
};

// Exported so a spec can assert this map covers every supported language. It is
// the map that actually fetches a catalog at runtime, so a language missing here
// renders English with no test failure and no console error — the one failure
// mode that looks exactly like success.
export const LAZY_CATALOG_LANGUAGES = Object.keys(lazyCatalogs);

// Moment locale data is code-split alongside the translation catalogs (static
// import strings so both webpack and vite can split the chunks). Moment formats
// every timestamp in the app (RelativeDateTime, TimestampFormatter, audit
// items), so its locale must follow the i18n language. Loading happens with the
// catalog so both are ready before `languageChanged` fires and Root remounts.
const lazyMomentLocales = {
  es: () => import('moment/locale/es'),
  de: () => import('moment/locale/de'),
  nl: () => import('moment/locale/nl'),
  fr: () => import('moment/locale/fr'),
  pl: () => import('moment/locale/pl'),
};

i18n
  .use(
    resourcesToBackend(language => {
      const load = lazyCatalogs[language];
      const loadMomentLocale = lazyMomentLocales[language] || (() => Promise.resolve());
      // Languages without a lazy catalog (e.g. en, already bundled) merge nothing.
      // A failed moment locale chunk must not block the translation catalog or
      // the language switch, so swallow its rejection independently.
      return Promise.all([
        load ? load().then(module => module.default || module) : Promise.resolve({}),
        loadMomentLocale().catch(() => undefined),
      ]).then(([catalog]) => catalog);
    })
  )
  .use(initReactI18next)
  .init({
    lng: 'en',
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    keySeparator: false,
    nsSeparator: false,
    partialBundledLanguages: true,
    resources: {
      en: { translation: enTranslation },
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

// localStorage key holding the agent's explicit UI-language choice. The
// detector reads it ahead of the browser language, and setLanguage() writes it,
// so an agent's pick survives reloads and follows them within this browser.
export const LANGUAGE_STORAGE_KEY = 'gladly-language';

// Persist the agent's UI-language choice and switch immediately. Writing the
// same key the detector reads means the choice is picked up ahead of the
// browser language on the next boot.
//
// Clamping to the supported set here is what lets AgentPreferences.uiLanguage be
// an unconstrained String: every path that persists a language (the picker, the
// preferences observer replaying a backend value) funnels through this function,
// so an unrecognized value can never become the sticky localStorage preference
// for a browser.
export function setLanguage(lng) {
  const language = resolveSupportedLanguage(lng);
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Storage blocked/unavailable — still switch for the current session.
  }
  // Swallow lazy catalog-load failures so a fire-and-forget switch (the
  // SetUiLanguage action, the preferences observer) never surfaces as an
  // unhandled rejection. The localStorage cache and persisted preference remain,
  // so the switch retries on the next load. Mirrors setAgentPreferences catching
  // its fire-and-forget gateway write.
  return i18n.changeLanguage(language).catch(() => {});
}

i18n.services.formatter.add('upperFirst', upperFirst);

// Set document direction and language attribute on language change
i18n.on('languageChanged', lng => {
  // `lng` is the raw detected code, which for real browsers carries a region
  // (e.g. `es-ES`, `es-419`). i18next resolves translations to the base
  // language via supportedLngs, so key moment locale + direction off the base
  // too — otherwise a region-suffixed locale gets Spanish text with
  // English-formatted dates. Fall back to en for languages without bundled
  // moment locale data.
  const baseLng = lng.split('-')[0];
  moment.locale(lazyMomentLocales[baseLng] ? baseLng : 'en');
  if (typeof document === 'undefined') return;
  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
  document.documentElement.dir = rtlLanguages.includes(baseLng) ? 'rtl' : 'ltr';
  // Keep the full tag on the lang attribute for correct HTML semantics.
  document.documentElement.lang = lng;
});

// Applies the agent's language: an explicit pick in localStorage, else the
// browser's languages. Called from app.js rather than run on import, so that
// loading this module never switches language on its own — importing it is how
// every spec and the admin app get an initialized instance, and neither should
// leave English. Resolves once the switch (and its catalog fetch) settles.
export function startLanguageDetection() {
  const detector = new LanguageDetector(i18n.services, {
    order: ['localStorage', 'navigator'],
    lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    caches: ['localStorage'],
  });
  const detected = detector.detect();
  if (!detected?.length) {
    return Promise.resolve();
  }
  // A failed catalog chunk leaves the UI in English rather than rejecting.
  return i18n.changeLanguage(detected).catch(() => {});
}

if (typeof window !== 'undefined') {
  window.i18n = i18n;
}

export default i18n;
